/** @jsx jsx */
import React, { useState, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { moreButton_softwareReleasesVersionDetailIssueList_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/moreButton_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import type { moreButton_softwareReleasesVersionDetailIssueList_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/moreButton_softwareReleasesVersionDetailIssueList_jiraVersion.graphql';
import MoveIssuesBetweenVersions from '@atlassian/jira-software-releases-move-issues-between-versions/src/async.tsx';
import { useMoveIssuesBetweenVersionsQuery } from '@atlassian/jira-software-releases-move-issues-between-versions/src/ui/index.tsx';
import { ISSUE_CARD_DATA_COMPONENT_SELECTOR } from '../../../../common/constants.tsx';
import messages from './messages.tsx';

type Props = {
	jiraIssue: moreButton_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	jiraVersion: moreButton_softwareReleasesVersionDetailIssueList_jiraVersion$key;
	hasWarnings: Boolean;
	hasOpenPullRequestWarning?: Boolean;
	hasFailingBuildsWarning?: Boolean;
	hasUnreviewedCodeWarning?: Boolean;
	hasOpenReviewWarning?: Boolean;
};

export const MoreButton = ({
	jiraIssue,
	jiraVersion,
	hasWarnings,
	hasOpenPullRequestWarning,
	hasFailingBuildsWarning,
	hasUnreviewedCodeWarning,
	hasOpenReviewWarning,
}: Props) => {
	const issue = useFragment(
		graphql`
			fragment moreButton_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				...ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue
				key
			}
		`,
		jiraIssue,
	);

	const version = useFragment(
		graphql`
			fragment moreButton_softwareReleasesVersionDetailIssueList_jiraVersion on JiraVersion {
				id @required(action: THROW)
				canAddAndRemoveIssues
				project @required(action: THROW) {
					id @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const [isPopupOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const [moveIssuesBetweenVersionsQueryReference, loadMoveIssuesBetweenVersionsQuery] =
		useMoveIssuesBetweenVersionsQuery(version.project.id, 4, version.id);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const togglePopup = () => {
		if (hasWarnings) {
			const warnings = [];
			if (hasOpenPullRequestWarning) {
				warnings.push('OPEN_PULL_REQUEST');
			}
			if (hasFailingBuildsWarning) {
				warnings.push('FAILING_BUILD');
			}
			if (hasUnreviewedCodeWarning) {
				warnings.push('UNREVIEWED_CODE');
			}
			if (hasOpenReviewWarning) {
				warnings.push('OPEN_REVIEW');
			}

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'IssueWarningMoreButton',
				}),
				{
					warnings: warnings.toString(),
				},
			);
		}

		if (moveIssuesBetweenVersionsQueryReference == null) {
			loadMoveIssuesBetweenVersionsQuery();
		}
		setIsOpen(!isPopupOpen);
	};

	const handlePopupClose = () => {
		setIsOpen(false);
	};

	return (
		<OnHoverVisibility>
			{(!!version.canAddAndRemoveIssues || hasWarnings) && (
				<MoveIssuesBetweenVersions
					renderTrigger={({ triggerProps }) => (
						<Button
							{...triggerProps}
							iconBefore={
								hasWarnings ? (
									<WarningIcon
										testId="software-releases-version-detail-issue-list.ui.issues.issue-card.more-button.issue-card-warning-more-button"
										color={token('color.icon.warning')}
										label={formatMessage(messages.moreVersions)}
										LEGACY_size="medium"
										spacing="spacious"
									/>
								) : (
									<MoreIcon
										spacing="spacious"
										label={formatMessage(messages.moreVersions, { issueKey: issue.key })}
									/>
								)
							}
							onClick={togglePopup}
							onMouseOver={loadMoveIssuesBetweenVersionsQuery}
							appearance="subtle"
							isSelected={isPopupOpen}
						/>
					)}
					queryReference={moveIssuesBetweenVersionsQueryReference}
					jiraIssue={issue}
					onClose={handlePopupClose}
					isOpen={isPopupOpen}
				/>
			)}
		</OnHoverVisibility>
	);
};

export const OnHoverVisibility = ({ children }: { children: ReactNode }) => (
	<div css={styles}>{children}</div>
);

const styles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${ISSUE_CARD_DATA_COMPONENT_SELECTOR}"]:hover & > *`]: {
		visibility: 'visible',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 5}px`,
});
